import { Switch } from '@headlessui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import usePushNotifications from '../../../hooks/usePushNotifications';
import useUser from '../../../hooks/useUser';
import { ProfilePreferenceTypeEnum } from '../../../proxies/UserProxy';
import { setPushPreference } from '../../../redux/ducks/StorageDucks';
import {
  userGetProfile,
  userUpdateProfilePreferenceRequest,
} from '../../../redux/ducks/UserDucks';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import Spinner from '../../layout/Spinner/Spinner';

const PreferencesBox = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentUser } = useUser();
  const {
    isPermitted: isPushPermitted,
    isSupported: isPushSupported,
    requestPermission: requestPushPermission,
  } = usePushNotifications();
  const [onUpdate, setOnUpdate] = useState<boolean>();

  const { updateProfilePreferenceRequest, updateProfilePreferenceResponse } =
    useSelector((state: RootReducerType) => state.user);

  const emailNotificationEnabled = useMemo<boolean>(() => {
    if (currentUser) {
      if (!currentUser?.preferences) {
        return false;
      }

      return (
        currentUser.preferences.filter(
          (p) =>
            p.type === ProfilePreferenceTypeEnum.UserEmailNotification &&
            p.value === '1',
        ).length > 0
      );
    }

    return false;
  }, [currentUser]);

  const pushNotificationEnabled = useMemo<boolean>(() => {
    if (!isPushSupported || !isPushPermitted) {
      return false;
    }

    if (currentUser) {
      if (!currentUser?.preferences) {
        return false;
      }

      return (
        currentUser.preferences.filter(
          (p) =>
            p.type === ProfilePreferenceTypeEnum.UserPushNotification &&
            p.value === '1',
        ).length > 0
      );
    }

    return false;
  }, [currentUser, isPushPermitted]);

  const onEmailSwitchChange = useCallback((enabled: boolean) => {
    setOnUpdate(true);
    dispatch(
      userUpdateProfilePreferenceRequest({
        type: ProfilePreferenceTypeEnum.UserEmailNotification,
        value: enabled ? '1' : '0',
      }),
    );
  }, []);

  const onPushSwitchChange = useCallback(async (enabled: boolean) => {
    setOnUpdate(true);
    dispatch(
      userUpdateProfilePreferenceRequest({
        type: ProfilePreferenceTypeEnum.UserPushNotification,
        value: enabled ? '1' : '0',
      }),
    );

    if (enabled) {
      requestPushPermission();
      dispatch(setPushPreference({ enable: true, updated: Date.now() }));
    } else {
      dispatch(setPushPreference({ enable: false, updated: Date.now() }));
    }
  }, []);

  useEffect(() => {
    if (onUpdate && updateProfilePreferenceResponse) {
      setOnUpdate(false);
      dispatch(userGetProfile(true));
    }
  }, [updateProfilePreferenceResponse, onUpdate]);

  return (
    <div className="flex min-w-full flex-col gap-8">
      <span className="text-lg font-bold">
        {t('static.account.preferences_section_title')}
      </span>
      <span className="flex flex-col gap-4">
        <span className="flex flex-row items-center justify-evenly">
          <span className="grow font-semibold">
            {t('static.account.preferences_email_notification_switch_label')}
          </span>
          <span className="pl-4">
            {onUpdate ? (
              <span
                className={
                  updateProfilePreferenceRequest &&
                  updateProfilePreferenceRequest.type ===
                    ProfilePreferenceTypeEnum.UserEmailNotification
                    ? 'block'
                    : 'hidden'
                }
              >
                <Spinner color="fill-sky-600" />
              </span>
            ) : (
              <Switch
                data-test-id="account_preferences_email_notification_switch"
                checked={emailNotificationEnabled}
                onChange={onEmailSwitchChange}
                className={`${
                  emailNotificationEnabled ? 'bg-sky-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 items-center rounded-full`}
              >
                <span
                  className={`${
                    emailNotificationEnabled ? 'translate-x-6' : 'translate-x-1'
                  } inline-block size-4 rounded-full bg-white`}
                />
              </Switch>
            )}
          </span>
        </span>
        <span className="flex flex-row items-center justify-evenly">
          <span className="grow font-semibold">
            {t('static.account.preferences_push_notification_switch_label')}
          </span>
          <span className="pl-4">
            {isPushSupported ? (
              onUpdate ? (
                <span
                  className={
                    updateProfilePreferenceRequest &&
                    updateProfilePreferenceRequest.type ===
                      ProfilePreferenceTypeEnum.UserPushNotification
                      ? 'block'
                      : 'hidden'
                  }
                >
                  <Spinner color="fill-sky-600" />
                </span>
              ) : (
                <Switch
                  data-test-id="account_preferences_push_notification_switch"
                  checked={pushNotificationEnabled}
                  onChange={onPushSwitchChange}
                  className={`${
                    pushNotificationEnabled ? 'bg-sky-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full`}
                >
                  <span
                    className={`${
                      pushNotificationEnabled
                        ? 'translate-x-6'
                        : 'translate-x-1'
                    } inline-block size-4 rounded-full bg-white`}
                  />
                </Switch>
              )
            ) : (
              <span className="text-sm font-semibold italic text-slate-300">
                {t('static.account.preferences_not_available_label')}
              </span>
            )}
          </span>
        </span>
      </span>
    </div>
  );
};

export default PreferencesBox;
